import { get } from 'lodash';

/** Party object fields on document forms — nested API errors surface on the parent select. */
const PARTY_OBJECT_FIELDS = ['accounting_customer_party', 'accounting_supplier_party'];

/**
 * API nested party paths that the form stores under different flat field names
 * (payload maps principle_id_trn / beneficiary_id back to these on submit).
 */
const FLAT_PARTY_FIELD_ALIASES = {
  seller_supplier_party: 'principle_id_trn',
  buyer_customer_party: 'beneficiary_id',
  'payment_means.payment_means_code': 'payment_means_code',
};

/** Nested API paths mapped to flat transaction-type form fields. */
const FLAT_TRANSACTION_TYPE_FIELD_ALIASES = {
  'invoice_period.start_date': 'invoice_period_start_date',
  'invoice_period.end_date': 'invoice_period_end_date',
  'invoice_period.description': 'invoice_period_description',
  'delivery.party_name': 'delivery_party_name',
  'delivery.terms': 'incoterms',
  'delivery.delivery_location.address.street_name': 'delivery_street_name',
  'delivery.delivery_location.address.city_name': 'delivery_city_name',
  'delivery.delivery_location.address.country_code': 'delivery_country_code',
  'delivery.delivery_location.address.country_subentity': 'delivery_country_subentity',
  'delivery.delivery_location.address.postal_zone': 'delivery_postal_zone',
};

const mapFlatFieldAlias = (fieldName, aliases) => {
  if (aliases[fieldName]) {
    return aliases[fieldName];
  }

  for (const [apiPath, formField] of Object.entries(aliases)) {
    if (fieldName === apiPath || fieldName.startsWith(`${apiPath}.`)) {
      return formField;
    }
  }

  return fieldName;
};

/**
 * Converts API bracket-indexed paths to react-hook-form dot notation.
 * e.g. document_lines[0].classified_tax_category.id -> document_lines.0.classified_tax_category.id
 * Nested party fields (e.g. accounting_customer_party.endpoint_scheme_id) collapse to the parent select.
 * Agent-billing party ids map to principle_id_trn / beneficiary_id.
 */
export const normalizeApiErrorPath = (key) => {
  if (!key) {
    return key;
  }

  let fieldName = key.replace(/\[(\d+)\]/g, '.$1');

  for (const partyField of PARTY_OBJECT_FIELDS) {
    if (fieldName === partyField || fieldName.startsWith(`${partyField}.`)) {
      return partyField;
    }
  }

  for (const [apiParty, formField] of Object.entries(FLAT_PARTY_FIELD_ALIASES)) {
    if (fieldName === apiParty || fieldName.startsWith(`${apiParty}.`)) {
      return formField;
    }
  }

  fieldName = mapFlatFieldAlias(fieldName, FLAT_TRANSACTION_TYPE_FIELD_ALIASES);

  if (fieldName === 'delivery.actual_delivery_date') {
    return 'supply_date';
  }

  return fieldName;
};

export const applyApiErrors = (setError, apiErrors = {}) => {
  if (!apiErrors || typeof apiErrors !== 'object') {
    return;
  }

  const messagesByField = {};

  Object.entries(apiErrors).forEach(([key, value]) => {
    if (typeof value !== 'string' || !value) {
      return;
    }

    const fieldName = normalizeApiErrorPath(key);

    if (!messagesByField[fieldName]) {
      messagesByField[fieldName] = [];
    }

    if (!messagesByField[fieldName].includes(value)) {
      messagesByField[fieldName].push(value);
    }
  });

  Object.entries(messagesByField).forEach(([fieldName, messages]) => {
    setError(fieldName, { message: messages.join(' * '), type: 'string' });
  });
};

/** Top-level form fields that can show API errors on invoice / credit note forms. */
export const DOCUMENT_FORM_ERROR_FIELD_ROOTS = new Set([
  'document_number',
  'issue_date',
  'issue_time',
  'due_date',
  'note',
  'document_currency_code',
  'tax_currency_code',
  'accounting_cost',
  'buyer_reference',
  'invoice_type_code',
  'credit_note_type_code',
  'discrepancy_response',
  'tax_point_date',
  'supply_date',
  'accounting_supplier_party',
  'accounting_customer_party',
  'principle_id_trn',
  'beneficiary_id',
  'document_lines',
  'allowances',
  'charges',
  'payment_means',
  'payment_terms',
  'delivery',
  'tax_exchange_rate',
  'invoice_period',
  'order_reference',
  'billing_reference',
  'profile_execution_id',
  'payee_party',
  'tax_representative_party',
  'attachments',
  'invoice_period_start_date',
  'invoice_period_end_date',
  'invoice_period_description',
  'delivery_party_name',
  'delivery_street_name',
  'delivery_city_name',
  'delivery_country_code',
  'delivery_country_subentity',
  'delivery_postal_zone',
  'incoterms',
]);

/**
 * Messages for API error keys that are not bound to visible document form fields
 * (e.g. document_status from resubmit validators).
 */
export const getUnmappedApiErrorMessages = (apiErrors = {}, knownFieldRoots = DOCUMENT_FORM_ERROR_FIELD_ROOTS) => {
  if (!apiErrors || typeof apiErrors !== 'object') {
    return [];
  }

  const messages = [];

  Object.entries(apiErrors).forEach(([key, value]) => {
    if (typeof value !== 'string' || !value) {
      return;
    }

    if (key === 'message') {
      messages.push(value);
      return;
    }

    const fieldName = normalizeApiErrorPath(key);
    const root = fieldName.split('.')[0];

    if (!knownFieldRoots.has(root) && !knownFieldRoots.has(fieldName)) {
      messages.push(value);
    }
  });

  return [...new Set(messages)];
};

/**
 * Applies field errors to RHF and snackbars any messages that would otherwise be invisible.
 */
export const surfaceApiErrors = ({ setFormError, dispatch, showSnackbarAction, apiErrors }) => {
  if (!apiErrors || typeof apiErrors !== 'object') {
    return;
  }

  if (typeof setFormError === 'function') {
    setFormError(apiErrors);
  }

  const unmapped = getUnmappedApiErrorMessages(apiErrors);

  if (unmapped.length && typeof dispatch === 'function' && showSnackbarAction) {
    dispatch(showSnackbarAction({ message: unmapped.join(' * '), severity: 'error' }));
  }
};

/**
 * Strips array prefix for modal sub-forms.
 * e.g. document_lines.0.classified_tax_category.id -> classified_tax_category.id
 */
export const stripArrayFieldPrefix = (path, arrayName, index) => {
  const prefix = `${arrayName}.${index}.`;

  if (path?.startsWith(prefix)) {
    return path.slice(prefix.length);
  }

  return path;
};

const collectMessagesFromNode = (node, messages = []) => {
  if (!node) {
    return messages;
  }

  if (typeof node.message === 'string' && node.message) {
    messages.push(node.message);
  }

  if (typeof node === 'object' && node !== null) {
    Object.entries(node).forEach(([key, value]) => {
      if (key === 'message' || key === 'type' || key === 'ref') {
        return;
      }

      collectMessagesFromNode(value, messages);
    });
  }

  return messages;
};

/**
 * Resolves a display string for a form field, including nested object errors
 * (e.g. accounting_customer_party.endpoint_scheme_id under accounting_customer_party).
 */
export const getFieldErrorMessage = (errors, name) => {
  const node = get(errors, name);

  if (!node) {
    return '';
  }

  const messages = [...new Set(collectMessagesFromNode(node))];

  return messages.join(' * ');
};

/**
 * Collects per-index error messages for a field-array root (e.g. document_lines).
 */
export const collectArrayFieldErrors = (errors, arrayName) => {
  const arrayErrors = get(errors, arrayName);

  if (!arrayErrors) {
    return [];
  }

  if (typeof arrayErrors.message === 'string' && arrayErrors.message) {
    return [{ index: null, messages: [arrayErrors.message] }];
  }

  if (!Array.isArray(arrayErrors)) {
    return [];
  }

  return arrayErrors
    .map((itemErrors, index) => {
      const messages = collectMessagesFromNode(itemErrors);

      if (!messages.length) {
        return null;
      }

      return { index, messages: [...new Set(messages)] };
    })
    .filter(Boolean);
};

/**
 * Builds a relative-field -> message map for a single array item (modal use).
 */
export const getArrayItemFieldErrors = (errors, arrayName, index) => {
  const itemErrors = get(errors, `${arrayName}.${index}`);

  if (!itemErrors) {
    return {};
  }

  const result = {};

  const walk = (node, prefix = '') => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (typeof node.message === 'string' && node.message) {
      result[prefix.replace(/^\./, '')] = node.message;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (key === 'message' || key === 'type' || key === 'ref') {
        return;
      }

      walk(value, prefix ? `${prefix}.${key}` : key);
    });
  };

  walk(itemErrors);

  return result;
};

export const hasArrayFieldErrors = (errors, arrayName) => collectArrayFieldErrors(errors, arrayName).length > 0;
